import { Linking } from "react-native";
import { api, setAccessToken } from "@/lib/api";
import { setStoredToken } from "@/lib/token-store";

function getRedirectTo(): string {
  if (typeof window !== "undefined" && window.location?.origin?.startsWith("http")) {
    return `${window.location.origin}/auth/callback`;
  }
  return "footslapping://auth/callback";
}

// Bare `Linking.openURL` backgrounds the whole app to hand off to system
// Safari, then relies on iOS relaunching us via the footslapping:// deep
// link. Right after sign-out, Apple/Google still have a cached session for
// the domain and redirect back almost instantly — often before the app has
// finished resuming from the background, so expo-router's linking listener
// isn't attached yet and the very first callback silently drops (a retry
// works because the app is already warm from the first attempt).
// `WebBrowser.openAuthSessionAsync` runs the flow in an in-process
// ASWebAuthenticationSession instead, so the redirect resolves straight back
// into this call — no app backgrounding, no relaunch race.
async function openNativeAuthSession(url: string, redirectTo: string): Promise<void> {
  let result: Awaited<ReturnType<typeof import("expo-web-browser").openAuthSessionAsync>>;
  try {
    const WebBrowser = await import("expo-web-browser");
    result = await WebBrowser.openAuthSessionAsync(url, redirectTo);
  } catch {
    await Linking.openURL(url);
    return;
  }

  if (result.type !== "success" || !("url" in result) || !result.url) return;

  const { parse } = await import("expo-linking");
  const cookie = parse(result.url).queryParams?.cookie;
  if (typeof cookie !== "string") return;

  const { router } = await import("expo-router");
  router.push({ pathname: "/auth/callback", params: { cookie } });
}

type SessionUser = { id: string; email: string; name: string; image?: string | null };

export async function signInWithPassword(email: string, password: string) {
  const { data, error } = await api.post<{ token: string; user: SessionUser }>(
    "/auth/sign-in/email",
    { email, password },
  );
  if (data?.token) {
    setAccessToken(data.token);
    setStoredToken(data.token);
  }
  return { data, error };
}

export async function signUpWithPassword(email: string, password: string, name: string) {
  const { data, error } = await api.post<{ token: string | null; user: SessionUser }>(
    "/auth/sign-up/email",
    { email, password, name },
  );
  if (data?.token) {
    setAccessToken(data.token);
    setStoredToken(data.token);
  }
  return { data, error };
}

export async function signInWithApple() {
  const redirectTo = getRedirectTo();

  const { data, error } = await api.post<{ url: string }>("/auth/sign-in/social", {
    provider: "apple",
    callbackURL: redirectTo,
  });

  if (error) return { error };

  if (data?.url) {
    if (typeof window !== "undefined" && window.location?.origin?.startsWith("http")) {
      window.location.href = data.url;
    } else {
      await openNativeAuthSession(data.url, redirectTo);
    }
  }

  return { data };
}

export async function signInWithGoogle() {
  const redirectTo = getRedirectTo();

  const { data, error } = await api.post<{ url: string }>("/auth/sign-in/social", {
    provider: "google",
    callbackURL: redirectTo,
  });

  if (error) return { error };

  if (data?.url) {
    if (typeof window !== "undefined" && window.location?.origin?.startsWith("http")) {
      window.location.href = data.url;
    } else {
      await openNativeAuthSession(data.url, redirectTo);
    }
  }

  return { data };
}

/**
 * Sign in with Google using a credential (ID token) from the GSI (Google Identity Services) popup.
 *
 * This sends the ID token directly to Better Auth's social sign-in endpoint,
 * which verifies the token and creates a session without a page redirect.
 */
export async function signInWithGoogleCredential(credential: string) {
  const { data, error } = await api.post<{
    token: string;
    user: { id: string; email: string; name: string; image?: string | null };
  }>("/auth/sign-in/social", {
    provider: "google",
    idToken: {
      token: credential,
    },
  });

  if (error) return { error };

  if (data?.token) {
    setAccessToken(data.token);
    setStoredToken(data.token);
  }

  return { data };
}
